import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Send, 
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  Mail,
  FileText,
  HelpCircle,
  MessageCircle,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Textarea } from '@vaiu/ui/textarea';
import { Label } from '@vaiu/ui/label';
import { Badge } from '@vaiu/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@vaiu/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vaiu/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@vaiu/ui/select';
import { Separator } from '@vaiu/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { platformApi } from '@/services/platformApi';
import { LoadingSpinner } from '@vaiu/ui';
import VersionInfo from '@/components/shared/VersionInfo';
import { formatDistanceToNow } from 'date-fns';
import type { SupportTicket } from '@/services/api/SupportApiClient';

const SupportPage: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // New ticket form
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    message: '',
    category: 'technical' as SupportTicket['category'],
    priority: 'medium' as SupportTicket['priority']
  });

  // View state
  const [activeTab, setActiveTab] = useState<'new' | 'my-tickets'>('new');

  useEffect(() => {
    if (user) {
      loadMyTickets();
    }
  }, [user]);

  const loadMyTickets = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await platformApi.support.getMyTickets();
      setTickets(response.tickets || []);
    } catch (error: any) {
      // Handle 404/405 errors gracefully - the endpoint might not be implemented yet
      if (error.message?.includes('404') || error.message?.includes('405') || error.message?.includes('Not Found')) {
        console.log('Support tickets endpoint not available yet, showing empty state');
        setTickets([]);
      } else if (error.message !== 'Authentication required - please log in again') {
        console.log('Error loading support tickets:', error.message);
        setTickets([]); // Silently fail and show empty state
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!ticketForm.subject.trim() || !ticketForm.message.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please provide both subject and message',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      const newTicket = await platformApi.support.createTicket({
        email: user?.email || 'anonymous@vaiu.app',
        subject: ticketForm.subject,
        message: ticketForm.message,
        category: ticketForm.category,
        priority: ticketForm.priority
      });

      toast({
        title: 'Success',
        description: 'Your support ticket has been submitted. We\'ll respond within 24 hours.',
      });

      // Reset form
      setTicketForm({
        subject: '',
        message: '',
        category: 'technical',
        priority: 'medium'
      });

      // Reload tickets and switch to my tickets tab
      if (user) {
        await loadMyTickets();
        setActiveTab('my-tickets');
        setSelectedTicket(newTicket);
      }
    } catch (error: any) {
      console.error('Failed to submit ticket:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit support ticket',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'waiting_response': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Support Center</h1>
              <p className="text-gray-600 mt-2">Get help with your account and transactions</p>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant="outline" className="px-3 py-1">
                <Clock className="w-3 h-3 mr-1" />
                24hr Response Time
              </Badge>
            </div>
          </div>
        </div>

        {/* System Information Card */}
        <Card className="mb-6 bg-gray-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">System Status</h3>
                  <p className="text-xs text-gray-600">All systems operational</p>
                </div>
              </div>
              <div className="text-right">
                <VersionInfo format="full" className="text-right" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Help Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/help')}>
            <CardContent className="p-6">
              <HelpCircle className="w-8 h-8 text-blue-600 mb-3" />
              <h3 className="font-semibold mb-1">Help Center</h3>
              <p className="text-sm text-gray-600">Browse FAQs and guides</p>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => window.open('mailto:support@vaiu.app')}>
            <CardContent className="p-6">
              <Mail className="w-8 h-8 text-green-600 mb-3" />
              <h3 className="font-semibold mb-1">Email Support</h3>
              <p className="text-sm text-gray-600">support@vaiu.app</p>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('new')}>
            <CardContent className="p-6">
              <MessageSquare className="w-8 h-8 text-purple-600 mb-3" />
              <h3 className="font-semibold mb-1">Submit Ticket</h3>
              <p className="text-sm text-gray-600">Create a support request</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card>
          <CardHeader>
            <CardTitle>Support Tickets</CardTitle>
            <CardDescription>Submit new tickets or view your existing support requests</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="new">New Ticket</TabsTrigger>
                <TabsTrigger value="my-tickets" disabled={!user}>
                  My Tickets {tickets.length > 0 && `(${tickets.length})`}
                </TabsTrigger>
              </TabsList>

              {/* New Ticket Tab */}
              <TabsContent value="new" className="space-y-6">
                {!user && (
                  <Alert className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Guest Submission</AlertTitle>
                    <AlertDescription>
                      You're submitting as a guest. <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/login')}>Log in</Button> to track your tickets.
                    </AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmitTicket} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <Select
                        value={ticketForm.category}
                        onValueChange={(value) => setTicketForm({...ticketForm, category: value as any})}
                      >
                        <SelectTrigger id="category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technical">Technical Issue</SelectItem>
                          <SelectItem value="billing">Billing & Payments</SelectItem>
                          <SelectItem value="feature">Feature Request</SelectItem>
                          <SelectItem value="bug">Bug Report</SelectItem>
                          <SelectItem value="feedback">General Feedback</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="priority">Priority</Label>
                      <Select
                        value={ticketForm.priority}
                        onValueChange={(value) => setTicketForm({...ticketForm, priority: value as any})}
                      >
                        <SelectTrigger id="priority">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low - General inquiry</SelectItem>
                          <SelectItem value="medium">Medium - Some impact</SelectItem>
                          <SelectItem value="high">High - Significant impact</SelectItem>
                          <SelectItem value="urgent">Urgent - Critical issue</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      placeholder="Brief description of your issue"
                      value={ticketForm.subject}
                      onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      placeholder="Please provide detailed information about your issue..."
                      value={ticketForm.message}
                      onChange={(e) => setTicketForm({...ticketForm, message: e.target.value})}
                      rows={8}
                      required
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" disabled={submitting}>
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Submit Ticket
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </TabsContent>

              {/* My Tickets Tab */}
              <TabsContent value="my-tickets" className="space-y-4">
                {!user ? (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Please <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/login')}>log in</Button> to view your tickets.
                    </AlertDescription>
                  </Alert>
                ) : loading ? (
                  <LoadingSpinner text="Loading your tickets..." />
                ) : tickets.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">You haven't submitted any tickets yet</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setActiveTab('new')}
                    >
                      Create Your First Ticket
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {tickets.map((ticket) => (
                      <Card 
                        key={ticket._id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => setSelectedTicket(ticket)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <Badge className={getPriorityColor(ticket.priority)}>
                                  {ticket.priority}
                                </Badge>
                                <Badge className={getStatusColor(ticket.status)}>
                                  {ticket.status.replace('_', ' ')}
                                </Badge>
                                <span className="text-sm text-gray-500">
                                  #{ticket.ticketNumber}
                                </span>
                              </div>
                              <h3 className="font-semibold mb-1">{ticket.subject}</h3>
                              <p className="text-sm text-gray-600 line-clamp-2">{ticket.message}</p>
                              <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                                <span className="flex items-center">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {formatDistanceToNow(new Date(ticket.createdAt), { addSuffix: true })}
                                </span>
                                {ticket.messageCount && ticket.messageCount > 0 && (
                                  <span className="flex items-center">
                                    <MessageCircle className="w-3 h-3 mr-1" />
                                    {ticket.messageCount} messages
                                  </span>
                                )}
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Ticket Detail Modal */}
        {selectedTicket && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-2xl max-h-[80vh] overflow-auto">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>Ticket #{selectedTicket.ticketNumber}</CardTitle>
                    <CardDescription>{selectedTicket.subject}</CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedTicket(null)}
                  >
                    ✕
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Badge className={getPriorityColor(selectedTicket.priority)}>
                    {selectedTicket.priority}
                  </Badge>
                  <Badge className={getStatusColor(selectedTicket.status)}>
                    {selectedTicket.status.replace('_', ' ')}
                  </Badge>
                </div>
                
                <Separator />
                
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <User className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium">You</span>
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(selectedTicket.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{selectedTicket.message}</p>
                  </div>

                  {selectedTicket.messages?.map((msg) => (
                    <div key={msg._id} className={`rounded-lg p-4 ${
                      msg.authorRole === 'user' ? 'bg-gray-50' : 'bg-blue-50'
                    }`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium">{msg.authorName}</span>
                        <Badge variant="outline" className="text-xs">
                          {msg.authorRole}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  ))}
                </div>

                {selectedTicket.status === 'resolved' && (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      This ticket has been resolved. If you need further assistance, please create a new ticket.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportPage;
